import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Link,
  Stack,
  Text,
  VStack,
} from 'native-base';
import { Controller } from 'react-hook-form';
import Input from 'ui/components/Input';
import { colors } from 'ui/theme/colors';
import { useRef } from 'react';
import { IModalRefProps } from '../Modals/Modal/types';
import { ICreateAccountProps } from './types';
import { ModalEmployeeNumber } from '../ModalEmployeeNumber';

export const CreateAccount = ({
  control,
  errors,
  hasExternalId,
  onOpenPrivacyPolicy,
  onOpenTermsOfService,
  onGoToSignIn,
}: ICreateAccountProps) => {
  const employeeNumberModalRef = useRef<IModalRefProps>(null);

  const handleOpenEmployeeNumberModal = () => {
    employeeNumberModalRef.current?.open();
  };

  return (
    <>
      <Stack w="full">
        <Flex flexDirection={{ md: 'row', base: 'column' }} style={{ gap: 16 }}>
          <Input
            name="firstName"
            control={control}
            placeholder="Type your first name"
            label="First Name"
            isRequired
            isFullWidth
            errorMessage={errors.firstName?.message}
            iconErrorMessage="info"
            leftIconColor={colors.muted[400]}
          />
          <Input
            isFullWidth
            name="lastName"
            control={control}
            placeholder="Type your last name"
            type="text"
            label="Last Name"
            leftIconColor={colors.muted[400]}
            isRequired
            errorMessage={errors.lastName?.message}
            iconErrorMessage="info"
          />
        </Flex>

        <Flex mt={2} flexDirection={{ base: 'column' }} style={{ gap: 12 }}>
          <Input
            name="email"
            control={control}
            placeholder="Type your email address"
            label="Email address"
            isRequired
            errorMessage={errors.email?.message}
            iconErrorMessage="info"
            leftIconName="mail"
            leftIconColor={colors.muted[400]}
          />
          <Input
            isRequired
            isFullWidth
            isPhoneInput
            control={control}
            name="phoneNumber"
            placeholder="Type your phone number"
            label="Phone Number"
            iconErrorMessage="info"
            backgroundColor="white"
            borderColor="muted.200"
            errorMessage={errors.phoneNumber?.message}
            leftIconColor={colors.muted[400]}
          />
        </Flex>

        {hasExternalId ? (
          <Flex mt={2}>
            <Input
              isFullWidth
              isRequired
              control={control}
              name="externalId"
              helpIcon
              helpIconColor={colors.gray[600]}
              handleOpenHelpIcon={handleOpenEmployeeNumberModal}
              iconErrorMessage="info"
              backgroundColor="white"
              borderColor="muted.200"
              label="Employee ID Number"
              errorMessage={errors.externalId?.message}
              placeholder="Type your employee id number"
              leftIconColor={colors.muted[400]}
              isNumeric
            />
          </Flex>
        ) : null}

        <Flex mt={2}>
          <Input
            name="password"
            control={control}
            placeholder="Create a password"
            type="password"
            label="Password"
            leftIconName="lock"
            leftIconColor={colors.muted[400]}
            isRequired
            errorMessage={errors.password?.message}
            iconErrorMessage="info"
            helperText={
              'Your password must contain at least 8 characters (uppercase, lowercase, numbers and special characters)'
            }
            showPasswordRules={true}
          />
        </Flex>

        <HStack alignItems="center">
          <Controller
            control={control}
            name="termsOfService"
            render={({ field: { onChange, value } }) => (
              <VStack>
                <HStack alignItems="center" space="3" mt="4">
                  <Checkbox
                    id={'termsOfService'}
                    onChange={onChange}
                    name="termsOfService"
                    value="true"
                    isChecked={value}
                    my={3}
                  >
                    <Text
                      maxW="100%"
                      fontSize={14}
                      fontWeight="400"
                      width="100%"
                    >
                      I read and accept the{' '}
                      <Link
                        color="darkText"
                        onPress={() => {
                          onChange('true');
                          onOpenTermsOfService();
                        }}
                      >
                        terms of service
                      </Link>{' '}
                      and{' '}
                      <Link
                        color="darkText"
                        onPress={() => {
                          onChange('true');
                          onOpenPrivacyPolicy();
                        }}
                      >
                        privacy policy.
                      </Link>
                    </Text>
                  </Checkbox>
                </HStack>
                {errors.termsOfService?.message ? (
                  <Text color="error.600" fontSize={12} mt={1}>
                    {errors.termsOfService?.message}
                  </Text>
                ) : null}
              </VStack>
            )}
          />
        </HStack>

        {onGoToSignIn ? (
          <VStack alignSelf="center" space="2" w="full" mt={4}>
            <Stack w="full">
              <Text mx="auto" mt="6" mb="2">
                You already have an account?
              </Text>
              <Button bgColor="white" onPress={onGoToSignIn}>
                <Text color="primary.600" fontSize={16} fontWeight="500">
                  Sign in
                </Text>
              </Button>
            </Stack>
          </VStack>
        ) : null}
      </Stack>

      <ModalEmployeeNumber modalRef={employeeNumberModalRef} />
    </>
  );
};
